import React, { useState, useEffect } from "react";
import { PlusCircle, Edit, Trash2, Save, X, Loader } from "lucide-react";
import { categoryService } from "../../../services/categoryService";

interface CategorySpecifications {
  gsm: string;
  size: string;
  sheets: string;
}

interface Category {
  id: string;
  name: string;
  description: string;
  specifications: CategorySpecifications;
  createdAt: Date;
  updatedAt: Date;
}

interface Brand {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
}

const initialCategoryState = {
  name: "",
  description: "",
  specifications: {
    gsm: "",
    size: "",
    sheets: "",
  },
};

const CategoryBrandManagement = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [newCategory, setNewCategory] = useState(initialCategoryState);
  const [newBrand, setNewBrand] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [categoriesData, brandsData] = await Promise.all([
          categoryService.getAllCategories(),
          categoryService.getAllBrands(),
        ]);
        setCategories(categoriesData);
        setBrands(brandsData);
      } catch (err) {
        setError("Failed to load data. Please try again.");
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleAddCategory = async () => {
    if (!newCategory.name) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const categoryId = await categoryService.addCategory({
        ...newCategory,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      // Refresh categories list
      const updatedCategories = await categoryService.getAllCategories();
      setCategories(updatedCategories);
      setNewCategory(initialCategoryState);
    } catch (err) {
      setError("Failed to add category");
      console.error("Error adding category:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddBrand = async () => {
    if (!newBrand) return;
    setIsSubmitting(true);
    setError(null);

    try {
      await categoryService.addBrand({
        name: newBrand,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      // Refresh brands list
      const updatedBrands = await categoryService.getAllBrands();
      setBrands(updatedBrands);
      setNewBrand("");
    } catch (err) {
      setError("Failed to add brand");
      console.error("Error adding brand:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await categoryService.deleteCategory(id);
      setCategories(categories.filter((cat) => cat.id !== id));
    } catch (err) {
      setError("Failed to delete category");
      console.error("Error deleting category:", err);
    }
  };

  const handleDeleteBrand = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this brand?")) {
      return;
    }

    try {
      setIsSubmitting(true);
      await categoryService.deleteBrand(id);
      setBrands(brands.filter((brand) => brand.id !== id));
      setSuccess("Brand deleted successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError("Failed to delete brand");
      console.error("Error deleting brand:", err);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleUpdateCategory = async (id: string) => {
    if (!editingCategory) return;
    setIsSubmitting(true);

    try {
      await categoryService.updateCategory(id, {
        ...editingCategory,
        updatedAt: new Date(),
      });

      // Refresh categories list
      const updatedCategories = await categoryService.getAllCategories();
      setCategories(updatedCategories);
      setEditingCategory(null);
    } catch (err) {
      setError("Failed to update category");
      console.error("Error updating category:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateBrand = async (id: string) => {
    if (!editingBrand) return;
    setIsSubmitting(true);

    try {
      await categoryService.updateBrand(id, {
        ...editingBrand,
        updatedAt: new Date(),
      });

      // Refresh brands list
      const updatedBrands = await categoryService.getAllBrands();
      setBrands(updatedBrands);
      setEditingBrand(null);
    } catch (err) {
      setError("Failed to update brand");
      console.error("Error updating brand:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-brand-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
      {error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Categories Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Categories</h2>

        {/* Add Category Form */}
        <div className="mb-6 border-b pb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Category Name
              </label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) =>
                  setNewCategory({ ...newCategory, name: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Description
              </label>
              <input
                type="text"
                value={newCategory.description}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    description: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Default GSM
              </label>
              <input
                type="text"
                value={newCategory.specifications.gsm}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    specifications: {
                      ...newCategory.specifications,
                      gsm: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Default Size
              </label>
              <input
                type="text"
                value={newCategory.specifications.size}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    specifications: {
                      ...newCategory.specifications,
                      size: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Default Sheets
              </label>
              <input
                type="text"
                value={newCategory.specifications.sheets}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    specifications: {
                      ...newCategory.specifications,
                      sheets: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          <button
            onClick={handleAddCategory}
            disabled={isSubmitting || !newCategory.name}
            className="mt-4 flex items-center px-4 py-2 bg-brand-blue-500 text-white rounded-lg
              hover:bg-brand-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <Loader className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <PlusCircle className="w-4 h-4 mr-2" />
            )}
            Add Category
          </button>
        </div>

        {/* Categories List */}
        <div className="space-y-4">
          {categories.map((category) => (
            <div key={category.id} className="border rounded-lg p-4">
              {editingCategory?.id === category.id ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={editingCategory.name}
                    onChange={(e) =>
                      setEditingCategory({
                        ...editingCategory,
                        name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => handleUpdateCategory(category.id)}
                      disabled={isSubmitting}
                      className="flex items-center px-3 py-1 bg-green-500 text-white rounded-lg
                        disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <Loader className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-1" />
                      )}
                      Save
                    </button>
                    <button
                      onClick={() => setEditingCategory(null)}
                      className="flex items-center px-3 py-1 bg-gray-500 text-white rounded-lg"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">{category.name}</h3>
                    <p className="text-sm text-gray-600">
                      {category.description}
                    </p>
                    <div className="mt-2 text-xs text-gray-500">
                      {Object.entries(category.specifications)
                        .filter(([_, value]) => value)
                        .map(([key, value]) => (
                          <span key={key} className="mr-4">
                            {key.toUpperCase()}: {value}
                          </span>
                        ))}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingCategory(category)}
                      className="p-1 text-gray-400 hover:text-blue-500"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Brands Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Brands</h2>

        {/* Add Brand Form */}
        <div className="mb-6 border-b pb-6">
          <div className="flex items-center space-x-4">
            <input
              type="text"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              placeholder="Enter brand name"
              className="flex-1 px-3 py-2 border rounded-lg"
            />
            <button
              onClick={handleAddBrand}
              disabled={isSubmitting || !newBrand}
              className="flex items-center px-4 py-2 bg-brand-blue-500 text-white rounded-lg
                hover:bg-brand-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <Loader className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <PlusCircle className="w-4 h-4 mr-2" />
              )}
              Add Brand
            </button>
          </div>
        </div>

        {/* Brands List */}
        <div className="grid grid-cols-2 gap-4">
          {brands.map((brand) => (
            <div key={brand.id} className="border rounded-lg p-4">
              {editingBrand?.id === brand.id ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={editingBrand.name}
                    onChange={(e) =>
                      setEditingBrand({
                        ...editingBrand,
                        name: e.target.value,
                      })
                    }
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                  <button
                    onClick={() => handleUpdateBrand(brand.id)}
                    disabled={isSubmitting}
                    className="p-2 text-green-500 disabled:text-gray-300 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => setEditingBrand(null)}
                    className="p-2 text-gray-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="font-medium">{brand.name}</span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingBrand(brand)}
                      className="p-1 text-gray-400 hover:text-blue-500"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteBrand(brand.id)}
                      className="p-1 text-gray-400 hover:text-red-500"
                      disabled={isSubmitting}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryBrandManagement;
